import type {
  IAssetTemplateDetail,
  IAssetTemplateStatus,
  IAssetTemplateInteraction,
} from '~/modules/asset-template/types';
import type { DisableableAction, ResourceItemProps } from '@shopify/polaris';
import type { IAssetTemplateRemoveConfirmState } from '../types';
import type { AssetTemplate } from '~/modules/shopify';

import {
  checkTemplateNoSpecifiedPageAvailable,
  getMultipleExperimentsTooltip,
  getOtherExperimentTooltip,
  getUsedCampaignByReason,
  getExperimentUsages,
} from '../utils';
import {
  getDefaultThumbnail,
  getLabelByTemplateType,
  parseLabelTemplate,
  parseTemplateInfoBy,
} from '~/utils';
import { useAssetTemplateContext, useAssetTemplatePickerContext } from '~/modules/asset-template';
import { getExistingCampaigns, getUsedPageIds } from '~/modules/campaign-group-template/helpers';
import { useHandleEditTemplate } from '~/modules/campaign-template';
import { useDataTemplateDisabled } from './useDataTemplateDisabled';
import { IDisableReasonKey } from '~/modules/asset-template/types';
import { useLocationHandlers } from '~/hooks/useLocationHandlers';
import { ProviderSource, TemplateType } from '~/types/template';
import { useCampaignNavigate } from '~/modules/campaign/hooks';
import { ICampaignType } from '~/modules/campaign/types';
import { CAMPAIGN_STATUS } from '~/constants/campaign';
import { useFeatureFlagChecker } from '~/modules/apps';
import { lastTimestampAt } from '~/utils/dateTime';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';

interface IAssetTemplateItemStateProps {
  template: AssetTemplate;
  onOpenRemoveConfirm?: (params: IAssetTemplateRemoveConfirmState) => void;
  /** Opens the list of every price experiment holding this template. */
  onOpenEditExperiments?: () => void;
}
interface IAssetTemplateItemStateResult {
  templateDetail: IAssetTemplateDetail;
  templateStatus: IAssetTemplateStatus;
  templateInteraction: IAssetTemplateInteraction | undefined;
}
export const useAssetTemplateItemState = (
  props: IAssetTemplateItemStateProps,
): IAssetTemplateItemStateResult => {
  const { t } = useTranslation();
  const { template, onOpenRemoveConfirm, onOpenEditExperiments } = props;
  const { defaultThemePages, modalAssetTemplate, campaigns } = useAssetTemplateContext();
  const { modalData, modalType } = modalAssetTemplate;
  const { isSelectedOnlyTabGP, usedTemplateSuffixes } = useAssetTemplatePickerContext();
  const { getTemplateStateDisable } = useAssetTemplatePickerContext();
  const { handleOpenPageShopifyInNewTab } = useLocationHandlers();
  const { onEditWithTemplateWithGPBy } = useHandleEditTemplate();
  const { onEditCampaign } = useCampaignNavigate();
  const { isEnabledTemplatePageTypeLoading } = useFeatureFlagChecker();
  const { assignedLoading } = useAssetTemplateContext();
  const { dataTemplateDisabled } = useDataTemplateDisabled();

  const templateDetail = useMemo<IAssetTemplateDetail>(() => {
    if (template.themePageInfo) return template.themePageInfo;

    const { providerSource, updated_at } = template;

    const templateInfo = parseTemplateInfoBy({ templateSuffix: template.key })!;
    const isDefaultByShopify = providerSource === ProviderSource.SHOPIFY_ORIGIN;

    return {
      label: parseLabelTemplate(templateInfo),
      thumbnail: getDefaultThumbnail(providerSource),
      updatedAt: lastTimestampAt(updated_at) || '',
      templateInfo,
      default: isDefaultByShopify,
      providerSource: templateInfo.providerSource!,
    };
  }, [template]);

  const templateStatus = useMemo<IAssetTemplateStatus>(() => {
    const isHomePage = templateDetail.templateInfo.type === TemplateType.HOME;
    const isOriginTemplate = templateDetail.providerSource === ProviderSource.SHOPIFY_ORIGIN;
    const isGPTemplateMadeDefault =
      templateDetail.providerSource === ProviderSource.GEMPAGESV7 && templateDetail.default;
    const isDefaultTemplateByProvider = isOriginTemplate; // TODO: check default template by theme page
    const isDefaultTemplateByThemePage = defaultThemePages?.some(
      (page) => page.default && page.type === templateDetail.templateInfo.type,
    );
    const isOverriddenByGemPages =
      !isSelectedOnlyTabGP && isOriginTemplate && isDefaultTemplateByThemePage;

    const existingCampaigns = getExistingCampaigns(template.key || '', campaigns || []);
    const usedPageIds = getUsedPageIds(existingCampaigns);

    return {
      isHomePage,
      isDefaultTemplateByProvider,
      isOverriddenByGemPages: !!isOverriddenByGemPages,
      isThemePageChildren: !!templateDetail.isChildren,
      isUsedByOtherCampaigns: existingCampaigns.length > 0,
      isGPTemplateMadeDefault,
      usedPageIds,
      totalUsedPageIds: usedPageIds.length,
      assignedPageCount: template.assigned?.total ?? 0,
    };
  }, [
    templateDetail,
    isSelectedOnlyTabGP,
    defaultThemePages,
    campaigns,
    template.key,
    template.assigned?.total,
  ]);

  const templateDisableState = useMemo(
    () =>
      getTemplateStateDisable({
        modalType,
        modalData,
        template,
        templateDetail,
        templateStatus,
      }),
    [getTemplateStateDisable, modalData, modalType, template, templateDetail, templateStatus],
  );

  const templateInteraction = useMemo<IAssetTemplateInteraction | undefined>(() => {
    if (isEnabledTemplatePageTypeLoading) {
      const pageType = templateDetail.templateInfo.type;
      if (pageType && assignedLoading[pageType]) {
        return {
          isDisabled: true,
          hidePageAssigned: true,
          helpText: `Getting assigned ${getLabelByTemplateType(pageType)}...`,
        };
      }
    }

    if (!templateDisableState) return;

    const detail = dataTemplateDisabled[templateDisableState];
    switch (detail?.key) {
      case IDisableReasonKey.IS_USED_FOR_OTHER_CAMPAIGN:
      case IDisableReasonKey.IS_USED_FOR_OTHER_CAMPAIGN_GROUP: {
        const usageDetails = usedTemplateSuffixes[template.key];
        const usedCampaign = getUsedCampaignByReason({ usageDetails, reason: detail.key });
        const { campaignId, campaignStatus, campaignName, campaignType } = usedCampaign;

        // Only a price or URL test can hold the same template several times over, since each holds
        // it through a record it runs on rather than through the suffix itself.
        const isResourceExperiment =
          campaignType === ICampaignType.PRICE || campaignType === ICampaignType.URL;
        const experimentUsages = isResourceExperiment ? getExperimentUsages(usageDetails) : [];

        if (experimentUsages.length > 1) {
          // Naming one experiment's status would misread the others, so the row names none of them
          // and hands the whole list to the Edit experiments modal.
          return {
            isDisabled: true,
            helpText: t('In multiple experiments'),
            shortcutActions: detail.isHasShortcut
              ? [
                  {
                    content: t('Edit experiments'),
                    onAction: () => onOpenEditExperiments?.(),
                  },
                ]
              : undefined,
            tooltipContent: t(getMultipleExperimentsTooltip(experimentUsages)),
          };
        }

        const shortcutActions: DisableableAction[] = [];

        if (campaignStatus === CAMPAIGN_STATUS.DRAFT && onOpenRemoveConfirm) {
          shortcutActions.push({
            content: t('Remove from experiment'),

            onAction: () => onOpenRemoveConfirm(usedCampaign),
          });
        }

        shortcutActions.push({
          content: t('Edit experiment'),
          onAction: () => onEditCampaign({ type: campaignType, id: campaignId }, { newTab: true }),
        });

        const helpText = detail.content({ campaignStatus, campaignName });
        return {
          isDisabled: true,
          helpText,
          shortcutActions: detail.isHasShortcut ? shortcutActions : undefined,
          tooltipContent: t(getOtherExperimentTooltip(campaignType)),
        };
      }
      case IDisableReasonKey.IS_TEMPLATE_USED_TO_TEST: {
        const { totalUsedPageIds, assignedPageCount } = templateStatus;

        const assignedTotal = assignedPageCount || 0;
        const assignedCount = totalUsedPageIds > 0 ? totalUsedPageIds : assignedPageCount;
        const isDisabled = checkTemplateNoSpecifiedPageAvailable({
          asset: template,
          campaigns,
        });
        const helpText = detail.content({
          assignedTotal,
          assignedCount,
          templateType: templateDetail.templateInfo.type ?? '',
        });

        return { isDisabled, helpText: helpText };
      }
      case IDisableReasonKey.IS_NO_ASSIGNED_BY_SHOPIFY: {
        const templateType = templateDetail.templateInfo.type;
        const helpText = detail.content({ templateType });

        // Home on Shopify only has a single template that can't be assigned, so there's
        // no actionable page to navigate to
        const isHomePage = templateType === TemplateType.HOME;
        const shortcutActions: ResourceItemProps['shortcutActions'] =
          detail.isHasShortcut && !isHomePage
            ? [
                {
                  content: t('Go to {{pageType}}s', {
                    pageType: getLabelByTemplateType(templateType),
                  }),
                  onAction: () => {
                    handleOpenPageShopifyInNewTab(`${templateType}s`);
                  },
                },
              ]
            : undefined;
        return {
          isDisabled: true,
          helpText,
          hidePageAssigned: detail.hidePageAssigned && templateStatus.isHomePage,
          shortcutActions,
        };
      }
      case IDisableReasonKey.IS_NO_ASSIGNED_BY_GEMPAGES: {
        const templateType = templateDetail.templateInfo.type;
        const helpText = detail.content({ templateType });

        const shortcutActions: ResourceItemProps['shortcutActions'] = detail.isHasShortcut
          ? [
              {
                content: t('Go to editor'),
                onAction: () => onEditWithTemplateWithGPBy(template.key),
              },
            ]
          : undefined;
        return {
          isDisabled: true,
          helpText,
          hidePageAssigned: detail.hidePageAssigned && templateStatus.isHomePage,
          shortcutActions,
        };
      }
      case IDisableReasonKey.IS_SELECTED:
      case IDisableReasonKey.IS_TEMPLATE_DRAFT:
      case IDisableReasonKey.IS_USED_FOR_CONTROL:
      case IDisableReasonKey.IS_USED_FOR_VARIANT:
      case IDisableReasonKey.IS_TEMPLATE_VARIANT_NOT_ASSIGNED: {
        const helpText = detail.content();
        return { isDisabled: true, helpText: helpText };
      }
    }
  }, [
    t,
    template,
    campaigns,
    templateStatus,
    templateDisableState,
    usedTemplateSuffixes,
    templateDetail.templateInfo.type,
    onEditCampaign,
    onEditWithTemplateWithGPBy,
    handleOpenPageShopifyInNewTab,
    isEnabledTemplatePageTypeLoading,
    assignedLoading,
    dataTemplateDisabled,
    onOpenRemoveConfirm,
    onOpenEditExperiments,
  ]);

  return {
    templateDetail,
    templateStatus,
    templateInteraction,
  };
};
